<template>
  <div class="activity-logs">
    <!-- Filters Section -->
    <b-card
      v-if="showFilters"
      class="mb-3"
    >
      <b-row class="g-3">
        <b-col
          cols="12"
          md="3"
        >
          <label class="form-label">نوع الحدث</label>
          <b-form-select
            v-model="filters.event"
            :options="eventOptions"
            @change="handleFilterChange"
          >
            <template #first>
              <b-form-select-option :value="null">
                جميع الأحداث
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <label class="form-label">من تاريخ</label>
          <b-form-input
            v-model="filters.from_date"
            type="date"
            @change="handleFilterChange"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <label class="form-label">إلى تاريخ</label>
          <b-form-input
            v-model="filters.to_date"
            type="date"
            @change="handleFilterChange"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="d-flex align-items-end"
        >
          <b-button
            variant="outline-secondary"
            class="w-100"
            @click="clearFilters"
          >
            <feather-icon
              icon="XIcon"
              size="14"
              class="mr-1"
            />
            مسح الفلاتر
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Loading State -->
    <div
      v-if="loading"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem;"
      />
      <p class="mt-3 text-muted">
        جاري تحميل سجل النشاطات...
      </p>
    </div>

    <!-- Error State -->
    <b-alert
      v-else-if="error"
      variant="danger"
      show
      class="mb-3"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="16"
        class="mr-1"
      />
      {{ error }}
    </b-alert>

    <!-- Empty State -->
    <div
      v-else-if="logs.length === 0"
      class="text-center py-5"
    >
      <feather-icon
        icon="FileTextIcon"
        size="48"
        class="text-muted mb-2"
      />
      <p class="text-muted">
        لا توجد سجلات نشاطات
      </p>
    </div>

    <!-- Activity Logs Timeline -->
    <div
      v-else
      class="activity-timeline"
    >
      <div
        v-for="(log, index) in logs"
        :key="log.id"
        class="activity-log-item"
        :class="{ 'last-item': index === logs.length - 1 }"
      >
        <!-- Timeline connector -->
        <div
          v-if="index < logs.length - 1"
          class="timeline-connector"
        />

        <!-- Timeline dot -->
        <div
          class="timeline-dot"
          :class="getEventClass(log.event, log.properties)"
        >
          <feather-icon
            :icon="getEventIcon(log.event, log.properties)"
            size="14"
          />
        </div>

        <!-- Log content -->
        <div class="log-content">
          <div class="log-header">
            <div class="d-flex justify-content-between align-items-start flex-wrap">
              <div class="log-description">
                <strong>{{ log.description }}</strong>
              </div>
              <b-badge
                :variant="getEventBadgeVariant(log.event, log.properties)"
                class="ml-2"
              >
                {{ getEventLabel(log.event, log.properties) }}
              </b-badge>
            </div>
          </div>

          <div class="log-meta">
            <div class="d-flex flex-wrap align-items-center mt-2">
              <span class="text-muted mr-3">
                <feather-icon
                  icon="UserIcon"
                  size="14"
                  class="mr-1"
                />
                {{ (log.causer && (log.causer.name || log.causer.email)) || 'مستخدم غير معروف' }}
              </span>
              <span class="text-muted">
                <feather-icon
                  icon="ClockIcon"
                  size="14"
                  class="mr-1"
                />
                {{ formatDate(log.created_at) }}
              </span>
            </div>
            <div
              v-if="getLogIpAddress(log) || getLogCoordinates(log)"
              class="log-geodata mt-1"
            >
              <div class="log-geodata-inner d-flex flex-wrap align-items-center">
                <span
                  v-if="getLogIpAddress(log)"
                  class="log-geodata-item text-muted"
                  :title="getLogIpAddress(log)"
                >
                  <feather-icon
                    icon="GlobeIcon"
                    size="14"
                    class="mr-1"
                  />
                  <span class="log-geodata-label">عنوان IP:</span>
                  <code class="log-geodata-value">{{ getLogIpAddress(log) }}</code>
                </span>
                <span
                  v-if="getLogCoordinates(log)"
                  class="log-geodata-item text-muted"
                >
                  <feather-icon
                    icon="MapPinIcon"
                    size="14"
                    class="mr-1"
                  />
                  <span class="log-geodata-label">الإحداثيات:</span>
                  <code class="log-geodata-value">{{ formatLogCoordinates(getLogCoordinates(log)) }}</code>
                </span>
                <b-button
                  v-if="getLogCoordinates(log)"
                  :href="getGoogleMapsUrlForLog(getLogCoordinates(log))"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline-primary"
                  size="sm"
                  class="log-geodata-map-btn"
                  title="فتح الموقع على خرائط Google"
                >
                  <feather-icon
                    icon="MapPinIcon"
                    size="16"
                  />
                </b-button>
              </div>
            </div>
          </div>

          <!-- Changes display for updated events -->
          <div
            v-if="log.event === 'updated' && log.properties && log.properties.old && log.properties.attributes"
            class="log-changes mt-1"
          >
            <b-card class="bg-light">
              <h6 class="mb-3">
                <feather-icon
                  icon="EditIcon"
                  size="16"
                  class="mr-1"
                />
                التغييرات:
              </h6>
              <div class="changes-list">
                <div
                  v-for="(change, changeIndex) in getFilteredChanges(log)"
                  :key="changeIndex"
                  class="change-item mb-2"
                >
                  <div class="change-field">
                    <strong>{{ getFieldLabel(change.key) }}:</strong>
                  </div>
                  <div class="change-values">
                    <div class="old-value">
                      <span class="badge badge-light-danger">قبل:</span>
                      <span class="ml-1">{{ formatValue(change.oldValue) }}</span>
                    </div>
                    <div class="new-value mt-1">
                      <span class="badge badge-light-success">بعد:</span>
                      <span class="ml-1">{{ formatValue(change.newValue) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </b-card>
          </div>

          <!-- Attributes display for created events -->
          <div
            v-if="log.event === 'created' && log.properties && log.properties.attributes"
            class="log-attributes mt-3"
          >
            <b-card class="bg-light">
              <h6 class="mb-3">
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-1"
                />
                البيانات المُنشأة:
              </h6>
              <div class="attributes-list">
                <div
                  v-for="(value, key) in log.properties.attributes"
                  :key="key"
                  class="attribute-item mb-2"
                >
                  <strong>{{ getFieldLabel(key) }}:</strong>
                  <span class="ml-2">{{ formatValue(value) }}</span>
                </div>
              </div>
            </b-card>
          </div>

          <!-- Deleted event display -->
          <div
            v-if="log.event === 'deleted' && log.properties && log.properties.old"
            class="log-deleted mt-3"
          >
            <b-card class="bg-light border-danger">
              <h6 class="mb-0 text-danger">
                <feather-icon
                  icon="TrashIcon"
                  size="16"
                  class="mr-1"
                />
                تم حذف: {{ (log.subject && log.subject.name) || ('#' + log.subject_id) }}
              </h6>
            </b-card>
          </div>

          <!-- Team updates display -->
          <div
            v-if="log.properties && log.properties.team_type"
            class="log-team-updates mt-3"
          >
            <b-card class="bg-light">
              <h6 class="mb-3">
                <feather-icon
                  :icon="getTeamIcon(log.properties.team_type)"
                  size="16"
                  class="mr-1"
                />
                {{ getTeamTypeLabel(log.properties.team_type) }}:
              </h6>
              <div class="team-changes">
                <div
                  v-if="getAddedTeamMembers(log).length > 0"
                  class="team-added mb-2"
                >
                  <span class="badge badge-light-success mr-1">تم الإضافة:</span>
                  <span
                    v-for="(memberId, index) in getAddedTeamMembers(log)"
                    :key="memberId"
                  >
                    {{ formatTeamMember(memberId) }}{{ index < getAddedTeamMembers(log).length - 1 ? '، ' : '' }}
                  </span>
                </div>
                <div
                  v-if="getRemovedTeamMembers(log).length > 0"
                  class="team-removed mb-2"
                >
                  <span class="badge badge-light-danger mr-1">تم الإزالة:</span>
                  <span
                    v-for="(memberId, index) in getRemovedTeamMembers(log)"
                    :key="memberId"
                  >
                    {{ formatTeamMember(memberId) }}{{ index < getRemovedTeamMembers(log).length - 1 ? '، ' : '' }}
                  </span>
                </div>
                <div
                  v-if="getAddedTeamMembers(log).length === 0 && getRemovedTeamMembers(log).length === 0"
                  class="team-unchanged"
                >
                  <span class="text-muted">لا توجد تغييرات في الأعضاء</span>
                </div>
              </div>
            </b-card>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div
      v-if="!loading && !error && logs.length > 0 && pagination.last_page > 1"
      class="mt-4"
    >
      <b-pagination
        v-model="currentPage"
        :total-rows="pagination.total"
        :per-page="pagination.per_page"
        :total-pages="pagination.last_page"
        align="center"
        class="mb-0"
        @change="handlePageChange"
      >
        <template #prev-text>
          <feather-icon
            icon="ChevronRightIcon"
            size="16"
          />
        </template>
        <template #next-text>
          <feather-icon
            icon="ChevronLeftIcon"
            size="16"
          />
        </template>
      </b-pagination>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BSpinner,
  BBadge,
  BAlert,
  BFormSelect,
  BFormSelectOption,
  BFormInput,
  BButton,
  BRow,
  BCol,
  BPagination,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import activityLogsApi from '@/api/activity-logs/activityLogs'
import { mapGetters } from 'vuex'

export default {
  name: 'ActivityLogs',
  components: {
    BCard,
    BSpinner,
    BBadge,
    BAlert,
    BFormSelect,
    BFormSelectOption,
    BFormInput,
    BButton,
    BRow,
    BCol,
    BPagination,
    FeatherIcon,
  },
  props: {
    modelType: {
      type: String,
      default: null,
    },
    modelId: {
      type: [Number, String],
      default: null,
    },
    userId: {
      type: [Number, String],
      default: null,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    perPage: {
      type: Number,
      default: 15,
    },
    autoLoad: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      logs: [],
      loading: false,
      error: null,
      filters: {
        event: null,
        from_date: null,
        to_date: null,
      },
      pagination: {
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1,
      },
      eventOptions: [
        { value: 'created', text: 'إنشاء' },
        { value: 'updated', text: 'تحديث' },
        { value: 'deleted', text: 'حذف' },
        { value: 'restored', text: 'استعادة' },
      ],
    }
  },
  computed: {
    currentPage: {
      get() {
        return this.pagination.current_page
      },
      set(value) {
        this.pagination.current_page = value
      },
    },
    ...mapGetters({
      employees: 'employees/employees',
    }),
  },
  watch: {
    modelId() {
      if (this.autoLoad) {
        this.fetchLogs()
      }
    },
    userId() {
      if (this.autoLoad) {
        this.fetchLogs()
      }
    },
  },
  mounted() {
    if (this.autoLoad) {
      this.fetchLogs()
    }
  },
  methods: {
    async fetchLogs() {
      this.loading = true
      this.error = null

      try {
        const params = {
          per_page: this.perPage,
          page: this.pagination.current_page,
        }

        // Add filters
        if (this.filters.event) {
          params.event = this.filters.event
        }
        if (this.filters.from_date) {
          params.from_date = this.filters.from_date
        }
        if (this.filters.to_date) {
          params.to_date = this.filters.to_date
        }

        let response
        if (this.modelType && this.modelId) {
          // Fetch logs for specific model
          response = await activityLogsApi.getByModel(this.modelType, this.modelId, params)
        } else if (this.userId) {
          // Fetch logs for specific user
          response = await activityLogsApi.getByUser(this.userId, params)
        } else {
          // Fetch all logs
          if (this.modelType) {
            params.subject_type = this.modelType
          }
          if (this.modelId) {
            params.subject_id = this.modelId
          }
          response = await activityLogsApi.list(params)
        }

        // Handle response structure
        if (response && response.status === 'success') {
          this.logs = response.data || []
          if (response.meta) {
            this.pagination = {
              current_page: response.meta.current_page || 1,
              per_page: response.meta.per_page || this.perPage,
              total: response.meta.total || 0,
              last_page: response.meta.last_page || 1,
            }
          }
        } else {
          this.logs = response.data || response || []
        }
      } catch (error) {
        console.error('Error fetching activity logs:', error)
        this.error = (error.response && error.response.data && error.response.data.message)
          || 'حدث خطأ أثناء تحميل سجل النشاطات'
        this.logs = []
      } finally {
        this.loading = false
      }
    },
    handleFilterChange() {
      this.pagination.current_page = 1
      this.fetchLogs()
    },
    clearFilters() {
      this.filters = {
        event: null,
        from_date: null,
        to_date: null,
      }
      this.handleFilterChange()
    },
    handlePageChange(page) {
      this.pagination.current_page = page
      this.fetchLogs()
    },
    getEventClass(event, properties) {
      // Handle team updates
      if (!event && properties && properties.team_type) {
        return 'event-updated'
      }
      const classes = {
        created: 'event-created',
        updated: 'event-updated',
        deleted: 'event-deleted',
        restored: 'event-restored',
      }
      return classes[event] || 'event-default'
    },
    getEventIcon(event, properties) {
      // Handle team updates
      if (!event && properties && properties.team_type) {
        return 'UsersIcon'
      }
      const icons = {
        created: 'PlusIcon',
        updated: 'EditIcon',
        deleted: 'TrashIcon',
        restored: 'RotateCcwIcon',
      }
      return icons[event] || 'FileTextIcon'
    },
    getEventLabel(event, properties) {
      // Handle team updates
      if (!event && properties && properties.team_type) {
        return 'تحديث الفريق'
      }
      const labels = {
        created: 'إنشاء',
        updated: 'تحديث',
        deleted: 'حذف',
        restored: 'استعادة',
      }
      return labels[event] || event || 'حدث'
    },
    getEventBadgeVariant(event, properties) {
      // Handle team updates
      if (!event && properties && properties.team_type) {
        return 'info'
      }
      const variants = {
        created: 'success',
        updated: 'primary',
        deleted: 'danger',
        restored: 'warning',
      }
      return variants[event] || 'secondary'
    },
    formatDate(date) {
      if (!date) return '-'
      const dateObj = new Date(date)
      const now = new Date()
      const diffMs = now - dateObj
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      // Relative time for recent dates
      if (diffMins < 1) return 'الآن'
      if (diffMins < 60) return `منذ ${diffMins} دقيقة`
      if (diffHours < 24) return `منذ ${diffHours} ساعة`
      if (diffDays < 7) return `منذ ${diffDays} يوم`

      // Full date for older dates
      return dateObj.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatValue(value) {
      if (value === null || value === undefined) return '-'
      if (typeof value === 'boolean') return value ? 'نعم' : 'لا'
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value, null, 2)
        } catch (e) {
          return String(value)
        }
      }
      return String(value)
    },
    getFieldLabel(key) {
      // Map common field names to Arabic labels
      const labels = {
        code: 'الكود',
        name: 'الاسم',
        description: 'الوصف',
        notes: 'الملاحظات',
        status: 'الحالة',
        project_status: 'حالة المشروع',
        current_execution_status: 'حالة التنفيذ الحالية',
        level: 'المستوى',
        accuracy_degree: 'درجة الدقة',
        responsible_employee_id: 'الموظف المسؤول',
        latitude: 'خط العرض',
        longitude: 'خط الطول',
        etm_est: 'ETM East',
        etm_north: 'ETM North',
        etm_zone: 'ETM Zone',
        utm_est: 'UTM East',
        utm_north: 'UTM North',
        utm_zone: 'UTM Zone',
        coord_type: 'نوع الإحداثيات',
        index: 'المنسوب',
        governorate_id: 'المحافظة',
        center_id: 'الجهاز / الحي / المركز',
        city_id: 'الوحدة المحلية / المدينة',
        district_id: 'القرية / التجمع',
        village_id: 'عزبة / نجع / منطقة',
        customer_id: 'العميل',
        employee_id: 'الموظف',
        user_id: 'المستخدم',
        amount: 'المبلغ',
        cost: 'التكلفة',
        price: 'السعر',
        date: 'التاريخ',
        created_at: 'تاريخ الإنشاء',
        updated_at: 'تاريخ التحديث',
      }
      return labels[key] || key
    },
    shouldShowChange(key, oldValue, newValue) {
      // Only show changes where values actually differ
      if (oldValue === undefined || oldValue === null) {
        return newValue !== undefined && newValue !== null
      }
      return oldValue !== newValue
    },
    getFilteredChanges(log) {
      if (!log.properties || !log.properties.attributes || !log.properties.old) {
        return []
      }
      const changes = []
      const { attributes } = log.properties
      const oldValues = log.properties.old

      Object.keys(attributes).forEach(key => {
        const oldValue = oldValues[key]
        const newValue = attributes[key]
        if (this.shouldShowChange(key, oldValue, newValue)) {
          changes.push({
            key,
            oldValue,
            newValue,
          })
        }
      })

      return changes
    },
    // Public method to refresh logs
    refresh() {
      this.fetchLogs()
    },
    getTeamTypeLabel(teamType) {
      const labels = {
        gis_teams: 'فريق GIS / المكتب الفني',
        geodesia_teams: 'فريق الجيوديسيا',
        drawing_teams: 'فريق الرسم',
        inspection_teams: 'فريق المعاينة',
        qc_teams: 'فريق مراقبة الجودة',
        upload_on_platform_teams: 'فريق الرفع على المنصة',
      }
      return labels[teamType] || teamType
    },
    getTeamIcon(teamType) {
      const icons = {
        gis_teams: 'MapIcon',
        geodesia_teams: 'NavigationIcon',
        drawing_teams: 'EditIcon',
        inspection_teams: 'EyeIcon',
        qc_teams: 'CheckCircleIcon',
        upload_on_platform_teams: 'UploadIcon',
      }
      return icons[teamType] || 'UsersIcon'
    },
    getAddedTeamMembers(log) {
      if (!log.properties || !log.properties.new_team_ids || !log.properties.old_team_ids) {
        return []
      }
      const newIds = Array.isArray(log.properties.new_team_ids) ? log.properties.new_team_ids : []
      const oldIds = Array.isArray(log.properties.old_team_ids) ? log.properties.old_team_ids : []
      return newIds.filter(id => !oldIds.includes(id))
    },
    getRemovedTeamMembers(log) {
      if (!log.properties || !log.properties.new_team_ids || !log.properties.old_team_ids) {
        return []
      }
      const newIds = Array.isArray(log.properties.new_team_ids) ? log.properties.new_team_ids : []
      const oldIds = Array.isArray(log.properties.old_team_ids) ? log.properties.old_team_ids : []
      return oldIds.filter(id => !newIds.includes(id))
    },
    formatTeamMember(memberId) {
      // Try to get employee name from store if available
      if (this.employees && this.employees.data) {
        const employee = this.employees.data.find(emp => emp.id === memberId)
        if (employee) {
          return employee.full_name || employee.name || `#${memberId}`
        }
      }
      // Also try direct store access
      const employees = this.$store.getters['employees/employees']
      if (employees && employees.data) {
        const employee = employees.data.find(emp => emp.id === memberId)
        if (employee) {
          return employee.full_name || employee.name || `#${memberId}`
        }
      }
      // Fallback to ID
      return `#${memberId}`
    },
    getLogIpAddress(log) {
      if (!log) return null
      const raw = log.ip_address
        ?? (log.properties && log.properties.ip_address)
      if (raw == null || String(raw).trim() === '') return null
      return String(raw).trim()
    },
    getLogCoordinates(log) {
      if (!log) return null
      const props = log.properties && typeof log.properties === 'object' ? log.properties : null
      let lat = log.latitude
      let lng = log.longitude
      if (log.location && typeof log.location === 'object') {
        if (lat == null) lat = log.location.latitude
        if (lng == null) lng = log.location.longitude
      }
      if (props) {
        if (props.location && typeof props.location === 'object') {
          if (lat == null) lat = props.location.latitude
          if (lng == null) lng = props.location.longitude
        }
        if (lat == null) lat = props.latitude
        if (lng == null) lng = props.longitude
      }
      const nlat = Number(lat)
      const nlng = Number(lng)
      if (!Number.isFinite(nlat) || !Number.isFinite(nlng)) return null
      return { lat: nlat, lng: nlng }
    },
    formatLogCoordinates(coords) {
      if (!coords) return ''
      return `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`
    },
    getGoogleMapsUrlForLog(coords) {
      if (!coords) return '#'
      const q = `${coords.lat},${coords.lng}`
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`
    },
  },
}
</script>

<style scoped>
.activity-logs {
  padding: 1rem 0;
}

.activity-timeline {
  position: relative;
  padding-left: 2rem;
}

.activity-log-item {
  position: relative;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
}

.activity-log-item.last-item {
  margin-bottom: 0;
  padding-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: -2rem;
  top: 0.25rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 2;
}

.timeline-dot.event-created {
  background-color: #28c76f;
  color: white;
}

.timeline-dot.event-updated {
  background-color: #7367f0;
  color: white;
}

.timeline-dot.event-deleted {
  background-color: #ea5455;
  color: white;
}

.timeline-dot.event-restored {
  background-color: #ff9f43;
  color: white;
}

.timeline-connector {
  position: absolute;
  left: -1.5rem;
  top: 2.25rem;
  width: 2px;
  height: calc(100% - 0.5rem);
  background-color: #e0e0e0;
  z-index: 1;
}

.log-content {
  background: #fff;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.log-header {
  margin-bottom: 0.5rem;
}

.log-description {
  flex: 1;
}

.log-meta {
  font-size: 0.875rem;
}

.log-geodata {
  padding: 0.5rem 0.75rem;
  background: #f8f8f8;
  border-radius: 0.375rem;
  border: 1px solid #ebe9f1;
}

.log-geodata-inner {
  gap: 0.5rem 0.75rem;
}

.log-geodata-item {
  display: inline-flex;
  align-items: center;
  font-size: 0.8125rem;
}

.log-geodata-label {
  margin-inline-start: 0.25rem;
}

.log-geodata-value {
  margin-inline-end: 0.25rem;
  font-size: 0.8125rem;
  color: #5e5873;
  background: #fff;
  padding: 0.1rem 0.35rem;
  border-radius: 0.25rem;
  border: 1px solid #e9ecef;
}

.log-geodata-map-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  padding: 0.25rem 0.5rem;
}

.log-changes,
.log-attributes,
.log-deleted {
  margin-top: 1rem;
}

.change-item {
  padding: 0.5rem;
  background: #fff;
  border-radius: 0.25rem;
  border-left: 3px solid #7367f0;
}

.change-field {
  margin-bottom: 0.5rem;
  color: #5e5873;
}

.change-values {
  font-size: 0.875rem;
}

.old-value,
.new-value {
  display: flex;
  align-items: center;
}

.attribute-item {
  padding: 0.25rem 0;
  color: #5e5873;
}

.badge-light-danger {
  background-color: #ffeaea;
  color: #ea5455;
}

.badge-light-success {
  background-color: #e8f5e9;
  color: #28c76f;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .activity-timeline {
    padding-left: 1.5rem;
  }

  .timeline-dot {
    left: -1.5rem;
    width: 1.5rem;
    height: 1.5rem;
  }

  .timeline-connector {
    left: -1rem;
  }

  .log-content {
    padding: 0.75rem;
  }

  .log-description strong {
    font-size: 0.9rem;
  }
}
</style>
